import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { amountToWordsSoles } from './amountToWords';
import {
  getVentasDocumentTypeLabel,
  getVentasPaymentMethodLabel,
} from './ventasSalesConstants';
import { formatPrintDate } from './dateUtils';

const TEAL = [13, 148, 136];
const GRAY = [55, 65, 81];
const GRAY_MUTED = [107, 114, 128];
const GRAY_LIGHT = [156, 163, 175];
const BG_TABLE_ALT = [249, 250, 251];
const MARGIN = 14;

const fmtMoney = (n) =>
  parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUnitPrice = (p) =>
  parseFloat(p || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

const fmtQty = (value) =>
  parseFloat(value || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 3 });

function formatDate(value) {
  return formatPrintDate(value);
}

function docTypeLabel(entity) {
  if (entity?.document_type === 'ruc') return 'RUC';
  if (entity?.document_type === 'dni') return 'DNI';
  if (entity?.document_type) return String(entity.document_type).toUpperCase();
  return 'Documento';
}

function measureSegments(doc, segments) {
  let width = 0;
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.style === 'label' ? 'bold' : 'normal');
    doc.setFontSize(9);
    width += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return width;
}

function drawSegments(doc, startX, y, segments) {
  let x = startX;
  segments.forEach((seg) => {
    if (seg.style === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...TEAL);
    } else if (seg.style === 'sep') {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY_LIGHT);
    } else {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...GRAY);
    }
    doc.setFontSize(9);
    doc.text(seg.text, x, y);
    x += doc.getTextWidth(seg.text) + (seg.style === 'sep' ? 2.5 : 1.2);
  });
  return x;
}

function drawStatusBadge(doc, x, y, statusText, isIssued) {
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  const padX = 2.5;
  const textW = doc.getTextWidth(statusText);
  const badgeW = textW + padX * 2;
  const badgeH = 5;

  if (isIssued) {
    doc.setFillColor(209, 250, 229);
    doc.setTextColor(6, 95, 70);
  } else {
    doc.setFillColor(254, 226, 226);
    doc.setTextColor(153, 27, 27);
  }
  doc.roundedRect(x, y - badgeH + 1.2, badgeW, badgeH, 1.2, 1.2, 'F');
  doc.text(statusText, x + padX, y);
  return x + badgeW + 2;
}

function drawSectionTitle(doc, margin, y, title) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...TEAL);
  doc.text(title.toUpperCase(), margin, y);
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.4);
  doc.line(margin, y + 1.5, margin + doc.getTextWidth(title.toUpperCase()), y + 1.5);
}

function buildPaymentCondition(order) {
  if (order.payment_condition_type === 'cash') return 'Al contado';
  if (order.payment_condition_type === 'days') {
    return `Cada ${order.payment_condition_days} días`;
  }
  if (order.payment_condition_type === 'date' && order.payment_condition_date) {
    return `Fecha específica: ${formatDate(order.payment_condition_date)}`;
  }
  return '-';
}

function getItemDescription(item) {
  if (item.item_type === 'service' || item.service_id) {
    return item.service?.name || item.description || 'Servicio';
  }
  return item.product?.name || item.description || 'Producto';
}

function buildTableRows(order) {
  return (order.items || []).map((item) => [
    getItemDescription(item),
    item.unit_of_measure || item.product?.unit_of_measure || item.service?.unit_of_measure || 'UND',
    item.project ? `${item.project.code} - ${item.project.name}` : '-',
    fmtQty(item.quantity),
    `S/ ${fmtUnitPrice(item.unit_price)}`,
    `S/ ${fmtMoney(item.subtotal ?? item.total)}`,
  ]);
}

function getAmountsRightX(doc) {
  const table = doc.lastAutoTable;
  const subtotalCol = table?.table?.columns?.[5];
  if (subtotalCol) {
    return subtotalCol.x + subtotalCol.width - 3;
  }
  return MARGIN + 72 + 14 + 32 + 14 + 20 + 30 - 3;
}

function drawFooter(doc, order, startY, pageWidth, pageHeight, amountsRightX) {
  const contentWidth = pageWidth - MARGIN * 2;
  const obsWidth = contentWidth * 0.52;
  const totalsWidth = contentWidth * 0.44;
  const gap = contentWidth - obsWidth - totalsWidth;
  const footerHeight = 28;

  let y = startY;
  if (y + footerHeight > pageHeight - MARGIN) {
    doc.addPage();
    y = MARGIN + 8;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.setTextColor(...TEAL);
  doc.text('Observaciones:', MARGIN, y + 5);

  if (order.notes) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    const noteLines = doc.splitTextToSize(String(order.notes), obsWidth);
    doc.text(noteLines.slice(0, 4), MARGIN, y + 10);
  }

  const totalsX = MARGIN + obsWidth + gap;
  let ty = y + 5;
  const lineH = 4.8;

  const drawTotalLine = (label, value, { bold = false } = {}) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setFontSize(bold ? 9 : 8);
    doc.setTextColor(...(bold ? TEAL : GRAY_MUTED));
    doc.text(label, totalsX, ty);
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.setTextColor(...GRAY);
    doc.text(value, amountsRightX, ty, { align: 'right' });
    ty += lineH;
  };

  drawTotalLine('Base:', `S/ ${fmtMoney(order.base_imponible)}`);
  drawTotalLine('IGV:', `S/ ${fmtMoney(order.igv_amount)}`);
  if (parseFloat(order.no_gravados || 0) > 0) {
    drawTotalLine('No grav.:', `S/ ${fmtMoney(order.no_gravados)}`);
  }
  drawTotalLine('Total:', `S/ ${fmtMoney(order.total_amount)}`, { bold: true });

  const words = amountToWordsSoles(order.total_amount);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(7);
  doc.setTextColor(...GRAY_MUTED);
  const wordLines = doc.splitTextToSize(`Son: ${words}`, amountsRightX - totalsX);
  doc.text(wordLines.slice(0, 2), amountsRightX, ty + 1, { align: 'right' });
}

export async function downloadSalesOrderPdf(order) {
  if (!order) {
    throw new Error('No hay datos de la venta para generar el PDF');
  }

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  let y = MARGIN + 4;

  const title = String(getVentasDocumentTypeLabel(order.document_type) || 'Venta').toUpperCase();
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(...TEAL);
  doc.text(title, pageWidth / 2, y, { align: 'center' });

  y += 4;
  doc.setDrawColor(...TEAL);
  doc.setLineWidth(0.7);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 8;

  const seriesNumber =
    order.series && order.number
      ? `${order.series}-${order.number}`
      : order.series || order.number || '—';
  const statusText = order.status === 'cancelled' ? 'Cancelada' : 'Emitida';
  const isIssued = order.status !== 'cancelled';

  const line1 = [
    { text: 'Venta:', style: 'label' },
    { text: order.code || '-', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Serie/Núm.:', style: 'label' },
    { text: seriesNumber, style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Fecha:', style: 'label' },
    { text: formatDate(order.issue_date) || '—', style: 'value' },
    { text: '·', style: 'sep' },
    { text: 'Estado:', style: 'label' },
  ];
  const estadoX = MARGIN + measureSegments(doc, line1);
  drawSegments(doc, MARGIN, y, line1);
  drawStatusBadge(doc, estadoX, y, statusText, isIssued);

  y += 6;
  const clientLine = [
    { text: 'Cliente:', style: 'label' },
    { text: order.client?.name || 'N/A', style: 'value' },
  ];
  if (order.client?.document_number) {
    clientLine.push(
      { text: '·', style: 'sep' },
      { text: `${docTypeLabel(order.client)}:`, style: 'label' },
      { text: order.client.document_number, style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, clientLine);

  y += 6;
  const posLabel = order.point_of_sale
    ? [order.point_of_sale.code, order.point_of_sale.name].filter(Boolean).join(' — ')
    : '—';
  const posLine = [
    { text: 'Punto de venta:', style: 'label' },
    { text: posLabel, style: 'value' },
  ];
  if (order.payment_method) {
    posLine.push(
      { text: '·', style: 'sep' },
      { text: 'Pago:', style: 'label' },
      { text: getVentasPaymentMethodLabel(order.payment_method), style: 'value' },
    );
    if (order.operation_number) {
      posLine.push(
        { text: '·', style: 'sep' },
        { text: 'N° op.:', style: 'label' },
        { text: String(order.operation_number), style: 'value' },
      );
    }
  }
  drawSegments(doc, MARGIN, y, posLine);

  y += 6;
  const paymentLine = [
    { text: 'Condición de pago:', style: 'label' },
    { text: buildPaymentCondition(order), style: 'value' },
  ];
  if (order.payment_date) {
    paymentLine.push(
      { text: '·', style: 'sep' },
      { text: 'Fecha de pago:', style: 'label' },
      { text: formatDate(order.payment_date), style: 'value' },
    );
  }
  drawSegments(doc, MARGIN, y, paymentLine);

  const originCodes = [
    ...(order.sales_quotes || []).map((q) => q.code),
    ...(order.sales_continuous_services || []).map((s) => s.code),
  ].filter(Boolean);
  if (originCodes.length > 0) {
    y += 6;
    drawSegments(doc, MARGIN, y, [
      { text: 'Origen:', style: 'label' },
      { text: originCodes.join(', '), style: 'value' },
    ]);
  }

  y += 10;
  drawSectionTitle(doc, MARGIN, y, 'Items de la Venta');
  y += 6;

  const tableBody = buildTableRows(order);
  if (tableBody.length === 0) {
    tableBody.push(['Sin ítems registrados', '—', '—', '—', '—', '—']);
  }

  autoTable(doc, {
    startY: y,
    head: [['Descripción', 'Unidad', 'Proyecto', 'Cant.', 'P. Unit', 'Subtotal']],
    body: tableBody,
    margin: { left: MARGIN, right: MARGIN },
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
      textColor: GRAY,
      lineColor: [229, 231, 235],
      lineWidth: 0.2,
      valign: 'top',
    },
    headStyles: {
      fillColor: TEAL,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7.5,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 72 },
      1: { cellWidth: 14, halign: 'center' },
      2: { cellWidth: 32 },
      3: { cellWidth: 14, halign: 'right' },
      4: { cellWidth: 20,halign: 'right' },
      5: { cellWidth: 30,halign: 'right', fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: BG_TABLE_ALT },
    didDrawPage: (data) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...GRAY_LIGHT);
      doc.text(
        `Venta ${order.code || ''} — Pág. ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' },
      );
    },
  });

  const tableEndY = doc.lastAutoTable.finalY + 8;
  const amountsRightX = getAmountsRightX(doc);
  drawFooter(doc, order, tableEndY, pageWidth, pageHeight, amountsRightX);

  const safeCode = String(order.code || order.id || 'venta').replace(/[^\w.-]+/g, '_');
  doc.save(`Venta_${safeCode}.pdf`);
}
